import { useCallback, useState } from 'react';
import { 
    SortMap, 
    SortDirection,
    UseTableSortingParams, 
    UseTableSortingReturn 
} from '../types/table-types';

/**
 * Хук для управления сортировкой таблицы
 * 
 * @example
 * ```tsx
 * const { sort, handleSort, toggleSort, clearSort } = useTableSorting({
 *   onSortChange: (newSort) => fetchData(pagination.page, pagination.pageSize, newSort),
 * });
 * 
 * // В заголовке колонки
 * <th onClick={() => toggleSort('name')}>
 *   Имя {sort.name === 'ASC' ? '↑' : sort.name === 'DESC' ? '↓' : ''}
 * </th>
 * ```
 */
export const useTableSorting = ({
    initialSort = {},
    onSortChange,
}: UseTableSortingParams = {}): UseTableSortingReturn => {
    const [sort, setSort] = useState<SortMap>(initialSort);

    /**
     * Установить сортировку для поля
     */
    const handleSort = useCallback((field: string, direction: SortDirection) => {
        const newSort = { [field]: direction };
        setSort(newSort);
        onSortChange?.(newSort);
    }, [onSortChange]);

    /**
     * Переключить сортировку для поля (ASC -> DESC -> none)
     */
    const toggleSort = useCallback((field: string) => {
        setSort(prev => {
            let newSort: SortMap;
            
            if (prev[field] === 'ASC') {
                newSort = { [field]: 'DESC' };
            } else if (prev[field] === 'DESC') {
                newSort = {};
            } else {
                newSort = { [field]: 'ASC' };
            }
            
            onSortChange?.(newSort);
            return newSort;
        });
    }, [onSortChange]);

    /**
     * Очистить сортировку
     */
    const clearSort = useCallback(() => {
        setSort({});
        onSortChange?.({});
    }, [onSortChange]);

    return { 
        sort, 
        handleSort, 
        toggleSort, 
        clearSort,
        setSort,
    };
};

/**
 * Конвертировать сортировку Ant Design в формат API
 * 
 * @example
 * ```tsx
 * const handleTableChange = (pagination, filters, sorter) => {
 *   const apiSort = convertAntdSorterToApi(sorter);
 *   fetchData(pagination.current, pagination.pageSize, apiSort);
 * };
 * ```
 */
export const convertAntdSorterToApi = (sorter: {
    field?: string;
    order?: 'ascend' | 'descend' | null;
}): SortMap => {
    if (!sorter.field || !sorter.order) {
        return {};
    }
    
    return {
        [sorter.field]: sorter.order === 'ascend' ? 'ASC' : 'DESC',
    };
};

/**
 * Конвертировать сортировку API в формат Ant Design
 */
export const convertApiSortToAntd = (sort: SortMap): {
    field: string | undefined;
    order: 'ascend' | 'descend' | undefined;
} => {
    const entries = Object.entries(sort);
    
    if (entries.length === 0) {
        return { field: undefined, order: undefined };
    }
    
    const [field, direction] = entries[0];
    return {
        field,
        order: direction === 'ASC' ? 'ascend' : 'descend',
    };
};

export default useTableSorting;
